import { tr as __tr, useLocale as __useLocale, direction as __direction, localizedField as __localizedField } from "../i18n/runtime";
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Users, Calendar, CreditCard, BookOpen, FileText, PieChart, Settings, LogOut, ChevronDown, Plus, Eye, Edit, Trash2, Check, X, Menu, Plane } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import SettingsPage from './admin/SettingsPage';
import VisasPage from './admin/VisasPage';
import HotelsPage from './admin/HotelsPage';
import RecordsPage from './admin/RecordsPage';
import ContentPage from './admin/ContentPage';
import FinancePage from './admin/FinancePage';
import AdminNavigation from '../components/AdminNavigation';
import OperationsPage from './admin/OperationsPage';
const API = '/api';
const AdminDashboard = () => {
  __useLocale();
  const {
    user,
    token,
    logout,
    isAdmin,
    isStaff
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    language,
    toggleLanguage
  } = useLanguage();
  const [activeTab, setActiveTab] = useState(isAdmin ? 'overview' : 'bookings');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // داخل AdminDashboard.jsx
  const renderSettings = () => {
    return <SettingsPage />;
  };
  const renderVisas = () => {
    return <VisasPage />;
  };
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'staff'
  });
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    account_type: 'asset'
  });
  const [editingAccount, setEditingAccount] = useState(null);
  const headers = {
    Authorization: `Bearer ${token}`
  };
  useEffect(() => {
    fetchData();
  }, [activeTab]);
  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      if (activeTab === 'overview') {
        const res = await axios.get(`${API}/dashboard/stats.php`, {
          headers
        });
        setStats(res.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/records.php?resource=users`, {
          headers
        });
        setUsers(res.data);
      } else if (activeTab === 'bookings') {
        const res = await axios.get(`${API}/records.php?resource=bookings`, {
          headers
        });
        setBookings(res.data);
      } else if (activeTab === 'accounts') {
        const res = await axios.get(`${API}/records.php?resource=accounts`, {
          headers
        });
        setAccounts(res.data);
      }
    } catch (error) {
      setLoadError(__tr("تعذر تحميل البيانات. تحقق من اتصال القاعدة ثم أعد المحاولة."));
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  const sidebarItems = [{
    id: 'operations',
    icon: Calendar,
    label: "العمليات"
  }, {
    id: 'checkout-payments',
    icon: CreditCard,
    label: "مشتريات العملاء",
    adminOnly: true
  }, {
    id: 'hotels',
    icon: Settings,
    label: "الفنادق",
    adminOnly: true
  }, {
    id: 'countries',
    icon: Settings,
    label: "الدول",
    adminOnly: true
  }, {
    id: 'cities',
    icon: Settings,
    label: "المدن",
    adminOnly: true
  }, {
    id: 'airports',
    icon: Plane,
    label: "المطارات",
    adminOnly: true
  }, {
    id: 'buses',
    icon: Calendar,
    label: "الحافلات"
  }, {
    id: 'flights',
    icon: Plane,
    label: "تذاكر الطيران"
  }, {
    id: 'shipping',
    icon: Settings,
    label: "شركات النقل والشحن"
  }, {
    id: 'invoices',
    icon: FileText,
    label: "الفواتير"
  }, {
    id: 'receipts',
    icon: FileText,
    label: "سندات القبض",
    adminOnly: true
  }, {
    id: 'payment-vouchers',
    icon: FileText,
    label: "سندات الصرف",
    adminOnly: true
  }, {
    id: 'content',
    icon: FileText,
    label: "محتوى الموقع",
    adminOnly: true
  }, {
    id: 'overview',
    icon: LayoutDashboard,
    label: t('dashboard.overview'),
    adminOnly: true
  }, {
    id: 'users',
    icon: Users,
    label: t('dashboard.users'),
    adminOnly: true
  }, {
    id: 'bookings',
    icon: Calendar,
    label: t('dashboard.bookings')
  }, {
    id: 'payments',
    icon: CreditCard,
    label: t('dashboard.payments')
  }, {
    id: 'accounts',
    icon: BookOpen,
    label: t('dashboard.chartOfAccounts'),
    adminOnly: true
  }, {
    id: 'journal',
    icon: FileText,
    label: t('dashboard.journalEntries'),
    adminOnly: true
  }, {
    id: 'reports',
    icon: PieChart,
    label: t('dashboard.reports'),
    adminOnly: true
  }, {
    id: 'visas',
    icon: Settings,
    label: t('dashboard.visas'),
    adminOnly: true
  }, {
    id: 'settings',
    icon: Settings,
    label: t('dashboard.settings'),
    adminOnly: true
  }];
  const filteredItems = sidebarItems.filter(item => !item.adminOnly || isAdmin);
  useEffect(() => {
    const path = location.pathname.replace(/^\/admin\/?/, '');
    const aliases = {
      'services/hotels': 'hotels',
      'services/visas': 'visas',
      'services/tickets': 'flights',
      'services/buses': 'buses',
      'accounting/accounts': 'accounts',
      'accounting/journal': 'journal',
      'accounting/receipts': 'receipts',
      'accounting/payments': 'payment-vouchers'
    };
    const tab = aliases[path] || path || (isAdmin ? 'overview' : 'bookings');
    setActiveTab(tab);
  }, [location.pathname, isAdmin]);
  const renderOverview = () => <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title={__tr(t('dashboard.totalBookings'))} value={stats?.bookings_count || 0} icon={Calendar} color="bg-blue-500" />
        <StatCard title={__tr(t('dashboard.users'))} value={stats?.users_count || 0} icon={Users} color="bg-green-500" />
        <StatCard title={__tr(t('dashboard.payments'))} value={stats?.transactions_count || 0} icon={CreditCard} color="bg-purple-500" />
        <StatCard title={__tr(t('dashboard.totalRevenue'))} value={`$${Number(stats?.total_revenue || 0).toFixed(2)}`} icon={PieChart} color="bg-orange-500" />
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">{__tr(t('dashboard.recentBookings'))}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-right py-3 px-4 text-gray-600">{__tr("ID")}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr(t('booking.tripType'))}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr(t('status.pending'))}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr(t('dashboard.totalRevenue'))}</th>
              </tr>
            </thead>
            <tbody>
              {__tr(stats?.recent_bookings?.map(booking => <tr key={booking.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm">{__tr(booking.id?.slice(0, 8))}</td>
                  <td className="py-3 px-4 text-sm">{__tr(booking.booking_type)}</td>
                  <td className="py-3 px-4">
                    <StatusBadge status={booking.status} t={t} />
                  </td>
                  <td className="py-3 px-4 text-sm">${__tr(booking.total_amount)}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-gray-800">{__tr(t('dashboard.users'))}</h3>
        <Button className="bg-[#6B5B95] hover:bg-[#5B4B85]" onClick={() => setShowAddUser(true)}>
          <Plus className="w-4 h-4 ml-2" />
          {__tr(t('dashboard.addUser'))}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-right py-3 px-4 text-gray-600">{__tr(t('auth.fullName'))}</th>
              <th className="text-right py-3 px-4 text-gray-600">{__tr(t('auth.email'))}</th>
              <th className="text-right py-3 px-4 text-gray-600">{__tr(t('dashboard.users'))}</th>
              <th className="text-right py-3 px-4 text-gray-600">{__tr(t('common.actions'))}</th>
            </tr>
          </thead>
          <tbody>
            {__tr(users.map(u => <tr key={u.id} className="border-b hover:bg-gray-50">
                <td className="py-3 px-4">{__tr(u.full_name)}</td>
                <td className="py-3 px-4 text-sm">{__tr(u.email)}</td>
                <td className="py-3 px-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-red-100 text-red-700' : u.role === 'staff' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {__tr(u.role)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost"><Edit className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>;
  const handleSaveAccount = async e => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/accounting/show_accounts.php`, newAccount, {
        headers
      });
      if (res.data.status === "duplicate") {
        alert("Account code already exists under this parent");
        return;
      }
      if (res.data.status === "success") {
        await fetchData();
        setShowAddAccount(false);
        setEditingAccount(null);
        setNewAccount({
          code: '',
          name_ar: '',
          name_en: '',
          account_type: 'asset',
          parent_id: null
        });
      }
    } catch (error) {
      console.log(error);
      alert("Server error");
    }
  };
  const renderAccounts = () => <>
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-800">
            {__tr(t('dashboard.chartOfAccounts'))}
          </h3>

          <Button className="bg-[#6B5B95] hover:bg-[#5B4B85]" onClick={() => setShowAddAccount(true)}>
            <Plus className="w-4 h-4 ml-2" />
            {__tr(t('dashboard.addAccount'))}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-right py-3 px-4 text-gray-600">{__tr("الرمز")}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr("الاسم")}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr("النوع")}</th>
                <th className="text-right py-3 px-4 text-gray-600">{__tr("الرصيد")}</th>
              </tr>
            </thead>

            <tbody>
              {__tr(accounts.map(acc => <tr key={acc.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono">{__tr(acc.code)}</td>
                  <td className="py-3 px-4">
                    {__tr(language === 'ar' ? __localizedField(acc, "name_ar") : acc.name_en)}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${acc.account_type === 'asset' ? 'bg-green-100 text-green-700' : acc.account_type === 'liability' ? 'bg-red-100 text-red-700' : acc.account_type === 'equity' ? 'bg-blue-100 text-blue-700' : acc.account_type === 'revenue' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                      {__tr(acc.account_type)}
                    </span>
                  </td>
                  <td className="py-3 px-4">{__tr(Number(acc.balance || 0).toLocaleString(language))}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </div>

      {__tr(showAddAccount && <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <form onSubmit={handleSaveAccount} className="bg-white p-6 rounded-xl w-96">
            <h3 className="text-lg font-bold mb-4">
              {__tr(editingAccount ? t('dashboard.editAccount') : t('dashboard.addAccount'))}
            </h3>

            <input type="text" value={newAccount.code} className="w-full border p-2 mb-3 rounded bg-gray-100" readOnly />

            <input type="text" placeholder="Arabic Name" className="w-full border p-2 mb-3 rounded" value={newAccount.name_ar} onChange={e => setNewAccount({
          ...newAccount,
          name_ar: e.target.value
        })} required />

            <input type="text" placeholder="English Name" className="w-full border p-2 mb-3 rounded" value={newAccount.name_en} onChange={e => setNewAccount({
          ...newAccount,
          name_en: e.target.value
        })} required />

            <select className="w-full border p-2 mb-4 rounded" value={newAccount.account_type} onChange={e => setNewAccount({
          ...newAccount,
          account_type: e.target.value
        })}>
              <option value="asset">asset</option>
              <option value="liability">liability</option>
              <option value="equity">equity</option>
              <option value="revenue">revenue</option>
              <option value="expense">expense</option>
            </select>

            <div className="flex justify-end gap-2">

              <Button type="button" variant="ghost" onClick={() => {
            setShowAddAccount(false);
            setEditingAccount(null);
          }}>
                {__tr(t('common.cancel'))}
              </Button>

              <Button type="submit">
                {__tr(t('common.save'))}
              </Button>

            </div>
          </form>
        </div>)}
    </>;
  return <div className="admin-shell min-h-screen bg-gray-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Sidebar */}
      <aside className={`fixed top-0 right-0 h-[100dvh] w-64 flex flex-col bg-white shadow-lg transition-transform duration-300 z-40 md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Logo */}
        <div className="p-4 border-b flex shrink-0 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Plane className="w-8 h-8 text-[#FF8C42]" />
            <span className="text-xl font-bold text-[#6B5B95]">{__tr(t('brand'))}</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden">
            <Menu className="w-5 h-5" />
          </Button>
        </div>

        {/* Nav Items */}
        <AdminNavigation items={filteredItems} activeTab={activeTab} language={language} onNavigate={() => setSidebarOpen(false)} />

        {/* Logout */}
        <div className="shrink-0 p-3 border-t">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all">
            <LogOut className="w-5 h-5" />
            <span>{__tr(t('nav.logout'))}</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {__tr(sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-30 z-30 md:hidden" onClick={() => setSidebarOpen(false)}></div>)}

      {/* Main Content */}
      <main className="transition-all duration-300 md:mr-64 mr-0">
        {/* Header */}
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800">
            {__tr(t('dashboard.welcome'))}{__tr(", ")}{__tr(user?.full_name)}
          </h1>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={toggleLanguage}>
              {__tr(language === 'ar' ? 'EN' : "عربي")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden">
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {__tr(loadError && <div role="alert" className="text-red-700 p-4">{__tr(loadError)} <button className="underline" onClick={fetchData}>{__tr("إعادة المحاولة")}</button></div>)}
          {__tr(loading ? <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6B5B95]"></div>
            </div> : <>
              {__tr(activeTab === 'overview' && !loadError && renderOverview())}
              {__tr(['users', 'payments', 'countries', 'cities', 'airports', 'buses', 'flights', 'shipping'].includes(activeTab) && <RecordsPage resource={activeTab} />)}
              {__tr(['operations', 'bookings'].includes(activeTab) && <OperationsPage />)}
              {__tr(['accounts', 'invoices', 'journal', 'reports', 'receipts', 'payment-vouchers', 'checkout-payments'].includes(activeTab) && <FinancePage key={activeTab} view={activeTab === 'reports' ? 'trial' : activeTab} />)}
              {__tr(activeTab === 'hotels' && <HotelsPage />)}
              {__tr(activeTab === 'content' && <ContentPage />)}
              {__tr(activeTab === 'settings' && renderSettings())}
              {__tr(activeTab === 'visas' && renderVisas())}
            </>)}
        </div>
      </main>
    </div>;
};

// Helper Components
const StatCard = ({
  title,
  value,
  icon: Icon,
  color
}) => {
  __useLocale();
  return <div className="bg-white rounded-xl shadow-lg p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm">{__tr(title)}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{__tr(value)}</p>
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>;
};
const StatusBadge = ({
  status,
  t
}) => {
  __useLocale();
  const colors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700'
  };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
      {__tr(t(`status.${status}`))}
    </span>;
};
const BookingsTable = ({
  bookings,
  t
}) => {
  __useLocale();
  return <div className="bg-white rounded-xl shadow-lg p-6">
    <h3 className="text-lg font-bold text-gray-800 mb-4">{__tr(t('dashboard.bookings'))}</h3>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-right py-3 px-4 text-gray-600">{__tr("ID")}</th>
            <th className="text-right py-3 px-4 text-gray-600">{__tr(t('booking.tripType'))}</th>
            <th className="text-right py-3 px-4 text-gray-600">{__tr(t('booking.from'))}</th>
            <th className="text-right py-3 px-4 text-gray-600">{__tr(t('booking.to'))}</th>
            <th className="text-right py-3 px-4 text-gray-600">{__tr(t('status.pending'))}</th>
            <th className="text-right py-3 px-4 text-gray-600">{__tr(t('dashboard.totalRevenue'))}</th>
          </tr>
        </thead>
        <tbody>
          {__tr(bookings.map(booking => <tr key={booking.id} className="border-b hover:bg-gray-50">
              <td className="py-3 px-4 text-sm font-mono">{__tr(booking.id?.slice(0, 8))}</td>
              <td className="py-3 px-4 text-sm">{__tr(booking.booking_type)}</td>
              <td className="py-3 px-4 text-sm">{__tr(booking.from_location || '-')}</td>
              <td className="py-3 px-4 text-sm">{__tr(booking.to_location || '-')}</td>
              <td className="py-3 px-4"><StatusBadge status={booking.status} t={t} /></td>
              <td className="py-3 px-4 text-sm">${__tr(booking.total_amount)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  </div>;
};
export default AdminDashboard;
